import { useQsFilters } from "@dash/filters";
import { TextField } from "@dash/form";
import { Page, PageHeader } from "@dash/page";

import { ExportItemsOptions, exportItemsOptionsSerializer } from "@megaron/catalog-contracts";
import { useClientManager } from "@megaron/react-clients";

import { SyncBar } from "./SyncBar";
import { SyncResultView } from "./SyncResultsView";
import { useGoogleSheetsAccessToken } from "./useGoogleSheetsAccessToken";

export const ExportItemsPage = () => {
  const qs = useQsFilters(exportItemsOptionsSerializer, {}, "options");

  const googleSheetsToken = useGoogleSheetsAccessToken(!!qs.filters.googleSheets);

  const query = useClientManager("catalog")
    .exportItems()
    .useQuery({
      ...qs.filters,
      dryRun: true,
    });

  const commitMutation = useClientManager("catalog").exportItems().useMutation();

  const commit = async () => {
    if (qs.filters.googleSheets) qs.filters.googleSheets.accessToken = googleSheetsToken;

    const result = await commitMutation.mutateAsync({
      ...qs.filters,
      dryRun: false,
    });

    if (result.googleSheets?.spreadsheetId) {
      window.open(`https://docs.google.com/spreadsheets/d/${result.googleSheets.spreadsheetId}/edit`);
      window.open(
        `/crm/items/import?options={"googleSheets":{"spreadsheetId":"${result.googleSheets.spreadsheetId}"}}`,
      );
    }
  };

  return (
    <Page css={{ maxWidth: "100%", gap: "1rem", display: "flex", flexDirection: "column" }}>
      <PageHeader>Eksportuj produkty</PageHeader>
      <GoogleSheetsOptionsForm value={qs.filters.googleSheets} onChange={qs.setFilter("googleSheets")} />
      <SyncBar
        onCommit={commit}
        isLoading={query.isLoading || commitMutation.isLoading}
        isDisabled={!query.data?.operations.length}
      />

      <SyncResultView
        isLoading={query.isLoading}
        result={query.data}
        labels={{
          bcId: "Id BC",
          shopifyProductId: "Shopify productId",
          box: "Karton",
          brand: "Marka",
          cost: "Koszt STD",
          defaultProfit: "Domyślna marża",
          description: "Opis",
          gtin: "EAN",
          handle: "Handle",
          isArchived: "Zarchiwizowany?",
          isInAvailability: "Śledzenie dostępności",
          isLoyalty: "Produkt Specjalista",
          itemId: "Id",
          itemName: "Nazwa",
          layer: "Warstwa",
          massKg: "Masa kg",
          pallet: "Paleta",
          productGroup: "Grupa produktowa",
          productId: "Indeks produktu",
          size: "Wielkość",
          sizeUnit: "Jednostka sprzedaży",
          title: "Tytuł",
          shopifyVariantId: "Shopify variantId",
        }}
      />
    </Page>
  );
};

const GoogleSheetsOptionsForm = (props: {
  value: ExportItemsOptions["googleSheets"];
  onChange: (value: ExportItemsOptions["googleSheets"]) => void;
}) => {
  if (!props.value) return null;

  type Form = typeof props.value;

  const onChange =
    <TKey extends keyof Form>(field: TKey) =>
    (value: Form[TKey]) => {
      props.onChange({
        ...props.value!,
        [field]: value,
      });
    };

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <div>Pozostaw ID puste, aby utworzyć nowy dokument.</div>
      <div css={{ display: "flex", flexDirection: "row", gap: "0.5rem" }}>
        <TextField
          label="Id dokumentu"
          value={props.value.spreadsheetId ?? ""}
          onChange={(s) => onChange("spreadsheetId")(s || undefined)}
        />
        <TextField
          label="Arkusz"
          value={props.value.sheetTitle ?? ""}
          onChange={(s) => onChange("sheetTitle")(s || undefined)}
        />
      </div>
    </div>
  );
};
